import { onlineManager, type QueryClient } from '@tanstack/react-query';
import type { DesktopEvent } from '@kiipu/desktop-protocol';
import { acceptPlatformState, desktopBridge, platformState } from './client-platform';
import {
  clearDesktopCache,
  deactivateDesktopCache,
  initializeDesktopCache,
  DESKTOP_CACHE_CHANGED,
  desktopCacheIdentity,
  desktopCachePartition,
  ownsDesktopCacheStorageKey,
} from './desktop-cache';
import { resetDesktopReadingQueries } from './desktop-query-sync';
import { clearDesktopSearchStorage } from './desktop-search';
import { clearCollectionFilterPreferences } from './collection-filter-preferences';
import {
  acceptDesktopCaptures,
  clearDesktopCaptures,
  desktopCapturesSnapshot,
  loadDesktopCaptures,
} from './desktop-captures';
import {
  dispatchDesktopCommand,
  setDesktopCommandIdentity,
  workspaceCommands,
} from './desktop-commands';

/** Subscribe before the handshake so authentication and native navigation cannot race startup. */
export async function prepareDesktop(client: QueryClient) {
  const bridge = desktopBridge();
  if (!bridge) return;
  const pending: DesktopEvent[] = [];
  let receive: ((event: DesktopEvent) => void) | undefined;
  bridge.onEvent((event) => {
    if (receive) receive(event);
    else {
      if (pending.length >= 256) pending.shift();
      pending.push(event);
    }
  });
  const initial = await bridge.getState();
  acceptPlatformState(initial);
  clearDesktopCaptures();
  void loadDesktopCaptures().catch(() => undefined);
  setDesktopCommandIdentity(initial);
  document.documentElement.dataset.desktop = initial.windowKind;
  onlineManager.setOnline(initial.online);
  if (initial.user) initializeDesktopCache(initial.apiOrigin, initial.user.id);
  client.setQueryData(['session'], { user: initial.user, csrfToken: '', registrationOpen: true });
  return (navigate: (path: string) => void) => {
    let changes = Promise.resolve();
    let observedCache = desktopCacheIdentity();
    let streamVersion: number | undefined;
    const cacheChanged = () => {
      const identity = desktopCacheIdentity();
      if (identity === observedCache) return;
      observedCache = identity;
      void resetDesktopReadingQueries(client).catch(() => undefined);
    };
    receive = (event) => {
      if (event.type === 'state') {
        const previous = platformState();
        const identityChanged =
          previous?.user?.id !== event.state.user?.id ||
          previous?.apiOrigin !== event.state.apiOrigin;
        const captureIdentityChanged =
          identityChanged || previous?.sessionGeneration !== event.state.sessionGeneration;
        const revoked = identityChanged ? clearDesktopCache().catch(() => undefined) : undefined;
        if (captureIdentityChanged) clearDesktopCaptures();
        if (identityChanged) {
          clearDesktopSearchStorage();
          clearCollectionFilterPreferences();
          window.dispatchEvent(new Event('kiipu:clear-search'));
          streamVersion = undefined;
          deactivateDesktopCache();
          void client.cancelQueries();
          client.clear();
        }
        acceptPlatformState(event.state);
        if (captureIdentityChanged) void loadDesktopCaptures().catch(() => undefined);
        setDesktopCommandIdentity(event.state);
        onlineManager.setOnline(event.state.online);
        client.setQueryData(['session'], {
          user: event.state.user,
          csrfToken: '',
          registrationOpen: true,
        });
        changes = changes
          .catch(() => undefined)
          .then(async () => {
            if (identityChanged) {
              await revoked;
              if (platformState()?.user?.id !== event.state.user?.id) return;
              if (event.state.user)
                initializeDesktopCache(event.state.apiOrigin, event.state.user.id);
              navigate(
                !event.state.user
                  ? '/login'
                  : event.state.windowKind === 'settings'
                    ? '/settings/general'
                    : event.state.windowKind === 'reader'
                      ? '/desktop/reader'
                      : '/',
              );
            } else if (!previous?.online && event.state.online) await client.invalidateQueries();
          });
      } else if (event.type === 'navigate') navigate(event.path);
      else if (event.type === 'capture-changed') {
        const current = platformState();
        if (
          !current?.user ||
          (event.owner !== undefined && event.owner !== current.user.id) ||
          (event.generation !== undefined && event.generation !== current.sessionGeneration)
        )
          return;
        const previous = new Map(desktopCapturesSnapshot().map((job) => [job.id, job]));
        acceptDesktopCaptures(event.jobs);
        if (
          event.jobs.some(
            (job) =>
              job.itemId &&
              (previous.get(job.id)?.itemId !== job.itemId ||
                (['completed', 'partial'].includes(job.phase) &&
                  previous.get(job.id)?.phase !== job.phase)),
          )
        )
          void client.invalidateQueries({
            predicate: (query) =>
              ['items', 'item', 'snapshots', 'usage'].includes(String(query.queryKey[0])),
          });
      } else if (event.type === 'command') {
        dispatchDesktopCommand(event.command);
        if (
          workspaceCommands.includes(event.command) &&
          platformState()?.windowKind === 'main' &&
          !['/', '/search'].includes(window.location.pathname)
        )
          navigate('/');
      } else if (event.type === 'update') {
        const current = platformState();
        if (current) acceptPlatformState({ ...current, update: event.update });
      } else if (event.type === 'content-changed') {
        const current = platformState();
        if (event.owner !== current?.user?.id || event.generation !== current.sessionGeneration)
          return;
        changes = changes
          .catch(() => undefined)
          .then(async () => {
            const latest = platformState();
            if (event.owner !== latest?.user?.id || event.generation !== latest.sessionGeneration)
              return;
            // The renderer making the successful mutation clears shared IndexedDB once.
            // Other renderers react to its revision, rather than clearing in a feedback loop.
            if (event.purged) await resetDesktopReadingQueries(client);
            else
              await client.invalidateQueries({
                predicate: (query) =>
                  ['items', 'item', 'snapshots', 'folders', 'tags', 'usage'].includes(
                    String(query.queryKey[0]),
                  ),
              });
          });
      } else if (event.type === 'stream') {
        const current = platformState();
        if (
          current?.windowKind !== 'main' ||
          !current.user ||
          ![
            'message',
            'changed',
            'capture.completed',
            'capture.updated',
            'item.updated',
            'content-changed',
          ].includes(event.event)
        )
          return;
        try {
          const payload = JSON.parse(event.data) as { version?: number };
          if (typeof payload.version === 'number') {
            const previous = streamVersion;
            streamVersion = payload.version;
            if (previous === undefined || previous === payload.version) return;
          }
        } catch {
          /* Named library events can carry a non-version payload. */
        }
        const owner = current.user.id;
        const generation = current.sessionGeneration;
        changes = changes
          .catch(() => undefined)
          .then(async () => {
            const latest = platformState();
            if (latest?.user?.id !== owner || latest.sessionGeneration !== generation) return;
            await clearDesktopCache().catch(() => undefined);
          });
      } else if (event.type === 'cache-cleared' && event.kind === 'reading') {
        void clearDesktopCache().catch(() => undefined);
      }
    };
    pending.splice(0).forEach(receive);
    document.addEventListener(
      'click',
      (event) => {
        const anchor = (event.target as Element | null)?.closest?.('a[href]');
        if (
          !(anchor instanceof HTMLAnchorElement) ||
          event.defaultPrevented ||
          anchor.hasAttribute('download')
        )
          return;
        const url = new URL(anchor.href, window.location.href);
        if (
          url.protocol === 'mailto:' ||
          (['http:', 'https:'].includes(url.protocol) && url.origin !== window.location.origin)
        ) {
          event.preventDefault();
          void bridge.openExternal(url.href).catch(() => undefined);
        } else if (url.pathname.startsWith('/admin')) {
          event.preventDefault();
          void bridge
            .openExternal(new URL(url.pathname + url.search, platformState()!.apiOrigin).href)
            .catch(() => undefined);
        } else if (
          url.pathname.startsWith('/settings') &&
          platformState()?.windowKind !== 'settings'
        ) {
          event.preventDefault();
          void bridge.openSettings(url.pathname).catch(() => undefined);
        }
      },
      true,
    );
    window.addEventListener('storage', (event) => {
      if (ownsDesktopCacheStorageKey(event.key)) cacheChanged();
    });
    window.addEventListener(DESKTOP_CACHE_CHANGED, (event) => {
      if (
        (event as CustomEvent<{ partition: string }>).detail.partition === desktopCachePartition()
      )
        cacheChanged();
    });
  };
}
